'use client'

import { useId, useState } from 'react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { Label } from '@/components/ui/label'
import { commentCount } from '@/lib/review-view'
import { cn } from '@/lib/utils'

/**
 * Comment plus verdicts behind a confirm, restored from Driver-Auto-CC
 * (`2a4551d^:frontend/src/components/approver/ReviewForm.tsx`) and generalised
 * (plan 08 WP-VIS-06): any number of verdicts instead of two, each with its
 * own label, tone, consequence and comment rule, all from the caller. Two
 * shapes share the rules:
 *
 * - `VerdictDialog`: one verdict, the comment inside the dialog (the buyer
 *   page opens it from its toolbar buttons, plan 04 section 3.3).
 * - `ReviewForm`: the comment inline, the verdict buttons below it, each
 *   behind a confirm; read-only afterwards.
 *
 * Cancel takes the first focus, never the destructive button (Radix
 * AlertDialog), and success goes to a polite live region (Rule 14).
 */

export type VerdictTone = 'primary' | 'success' | 'outline' | 'destructive'

export interface Verdict<K extends string = string> {
  key: K
  /** Button label, e.g. "Approve". */
  label: string
  tone: VerdictTone
  icon?: React.ElementType
  /** Dialog title, e.g. "Reject invoice NPC-24117?". */
  title: string
  /** One sentence on what happens next. */
  consequence?: string | null
  /** Confirm button label, e.g. "Reject invoice". */
  confirmLabel?: string | null
  commentRequired?: boolean
  commentLabel?: string | null
}

export const VERDICT_BUTTON_CLASS: Record<VerdictTone, string> = {
  primary: '',
  // The Workbench's emerald Approve, in the success token.
  success: 'bg-success-strong text-white hover:bg-success-strong/90',
  outline: '',
  destructive: '',
}

export function verdictVariant(tone: VerdictTone): 'default' | 'outline' | 'destructive' {
  if (tone === 'outline') return 'outline'
  if (tone === 'destructive') return 'destructive'
  return 'default'
}

const textareaClass =
  'w-full rounded-lg border border-border bg-card p-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

function commentOk(verdict: Verdict | null, comment: string, minLength: number): boolean {
  if (!verdict?.commentRequired) return true
  return comment.trim().length >= Math.max(1, minLength)
}

/** The comment rule in words, under the box. */
function ruleText(verdict: Verdict | null, minLength: number): string {
  if (!verdict?.commentRequired) return 'Optional. The AP team sees your comment.'
  return minLength > 1 ? `Required, at least ${minLength} characters.` : 'Required.'
}

// ─── VerdictDialog ────────────────────────────────────────────────────────

export function VerdictDialog({
  verdict,
  open,
  onOpenChange,
  minLength,
  maxLength,
  onConfirm,
}: {
  verdict: Verdict | null
  open: boolean
  onOpenChange: (open: boolean) => void
  minLength: number
  maxLength: number
  /** Resolve on success; throw with a message to keep the dialog open and show it. */
  onConfirm: (comment: string) => Promise<void>
}) {
  const [saving, setSaving] = useState(false)
  return (
    <AlertDialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <AlertDialogContent>
        {/* The content unmounts on close, so every opening starts with an empty comment. */}
        {verdict && (
          <VerdictBody
            key={verdict.key}
            verdict={verdict}
            minLength={minLength}
            maxLength={maxLength}
            saving={saving}
            setSaving={setSaving}
            onDone={() => onOpenChange(false)}
            onConfirm={onConfirm}
          />
        )}
      </AlertDialogContent>
    </AlertDialog>
  )
}

function VerdictBody({
  verdict,
  minLength,
  maxLength,
  saving,
  setSaving,
  onDone,
  onConfirm,
}: {
  verdict: Verdict
  minLength: number
  maxLength: number
  saving: boolean
  setSaving: (saving: boolean) => void
  onDone: () => void
  onConfirm: (comment: string) => Promise<void>
}) {
  const id = useId()
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)
  const ok = commentOk(verdict, comment, minLength)

  const confirm = async () => {
    if (!ok) return
    setSaving(true)
    setError(null)
    try {
      await onConfirm(comment.trim())
      setSaving(false)
      onDone()
    } catch (err) {
      setSaving(false)
      setError(err instanceof Error ? err.message : 'Could not save your decision')
    }
  }

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle className='font-display'>{verdict.title}</AlertDialogTitle>
        {verdict.consequence && <AlertDialogDescription>{verdict.consequence}</AlertDialogDescription>}
      </AlertDialogHeader>
      <div className='space-y-1.5'>
        <Label htmlFor={`${id}-comment`}>{verdict.commentLabel ?? 'Comment'}</Label>
        <textarea
          id={`${id}-comment`}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
          maxLength={maxLength}
          aria-describedby={`${id}-help ${id}-count`}
          aria-required={Boolean(verdict.commentRequired)}
          aria-invalid={comment.length > 0 && !ok ? true : undefined}
          className={textareaClass}
        />
        <div className='flex justify-between gap-2 text-xs text-muted-foreground'>
          <span id={`${id}-help`}>{ruleText(verdict, minLength)}</span>
          <span id={`${id}-count`}>{commentCount(comment, maxLength)}</span>
        </div>
      </div>
      {error && (
        <p role='alert' className='text-sm text-danger-strong'>
          {error}
        </p>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
        <Button
          variant={verdictVariant(verdict.tone)}
          className={VERDICT_BUTTON_CLASS[verdict.tone]}
          disabled={!ok || saving}
          onClick={() => void confirm()}
        >
          {saving && <Icons.loader className='mr-1.5 h-4 w-4 animate-spin' aria-hidden='true' />}
          {saving ? 'Saving…' : verdict.confirmLabel ?? verdict.label}
        </Button>
      </AlertDialogFooter>
    </>
  )
}

// ─── ReviewForm ───────────────────────────────────────────────────────────

export interface ExistingReview<K extends string = string> {
  choice: K
  comment?: string | null
  submittedLabel?: string | null
}

export function ReviewForm<K extends string>({
  verdicts,
  commentLabel,
  helpText,
  minLength = 0,
  maxLength,
  existing,
  successMessage,
  onSubmit,
}: {
  verdicts: Verdict<K>[]
  commentLabel: string
  helpText: string
  minLength?: number
  maxLength: number
  existing?: ExistingReview<K> | null
  successMessage: (choice: K) => string
  onSubmit: (choice: K, comment: string) => Promise<void>
}) {
  const id = useId()
  const [comment, setComment] = useState(existing?.comment ?? '')
  const [pending, setPending] = useState<Verdict<K> | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [announce, setAnnounce] = useState('')

  const confirm = async () => {
    if (!pending) return
    setSaving(true)
    setError(null)
    try {
      await onSubmit(pending.key, comment.trim())
      setAnnounce(successMessage(pending.key))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your review')
    } finally {
      setSaving(false)
      setPending(null)
    }
  }

  const chosen = existing ? verdicts.find((v) => v.key === existing.choice) : null

  return (
    <div className='space-y-4'>
      <p aria-live='polite' className='sr-only'>
        {announce}
      </p>
      {existing ? (
        <div className='space-y-2 rounded-xl border border-hairline bg-card p-4'>
          <p className='text-sm text-foreground'>
            Your decision: <strong>{chosen?.label ?? existing.choice}</strong>
            {existing.submittedLabel ? ` · ${existing.submittedLabel}` : ''}
          </p>
          {existing.comment && <p className='text-sm text-foreground'>&ldquo;{existing.comment}&rdquo;</p>}
        </div>
      ) : (
        <>
          <div className='space-y-1.5'>
            <Label htmlFor={`${id}-comment`}>{commentLabel}</Label>
            <textarea
              id={`${id}-comment`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={5}
              maxLength={maxLength}
              aria-describedby={`${id}-help ${id}-count`}
              className={textareaClass}
            />
            <div className='flex justify-between gap-2 text-xs text-muted-foreground'>
              <span id={`${id}-help`}>{helpText}</span>
              <span id={`${id}-count`}>{commentCount(comment, maxLength)}</span>
            </div>
          </div>
          {error && (
            <p role='alert' className='text-sm text-danger-strong'>
              {error}
            </p>
          )}
          <div className='flex flex-wrap gap-3'>
            {verdicts.map((verdict) => {
              const Icon = verdict.icon
              return (
                <Button
                  key={verdict.key}
                  variant={verdictVariant(verdict.tone)}
                  className={cn(VERDICT_BUTTON_CLASS[verdict.tone])}
                  disabled={saving || !commentOk(verdict, comment, minLength)}
                  onClick={() => setPending(verdict)}
                >
                  {Icon && <Icon className='mr-1 h-4 w-4' aria-hidden='true' />}
                  {verdict.label}
                </Button>
              )
            })}
          </div>
        </>
      )}

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && !saving && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className='font-display'>{pending?.title ?? ''}</AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.consequence ? `${pending.consequence} ` : ''}
              {comment.trim() ? `Your comment: "${comment.trim()}"` : 'No comment added.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            {pending && (
              <Button
                variant={verdictVariant(pending.tone)}
                className={VERDICT_BUTTON_CLASS[pending.tone]}
                disabled={saving}
                onClick={() => void confirm()}
              >
                {saving ? 'Saving…' : pending.confirmLabel ?? pending.label}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
